import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { X } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import { cn } from '@/utils/cls';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Width preset — `sm` ≈ 380, `md` ≈ 480, `lg` ≈ 720 */
  size?: 'sm' | 'md' | 'lg';
  /** Optional footer slot — usually the Cancel/Confirm buttons */
  footer?: ReactNode;
}

const SIZES = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl' } as const;

/** A11y-correct dialog. Uses Headless UI for focus trap + ESC + click-outside. */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
  footer,
}: ModalProps) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm" aria-hidden="true" />
        </TransitionChild>

        {/*
          Outer container: a vertically-scrollable backdrop that centers
          the panel. We layer two strategies so even very tall content on
          a short viewport stays usable:
            1. The outer DIV is `overflow-y-auto`, so if the panel itself
               is taller than the viewport (e.g. a giant form on a
               landscape phone), the whole thing can scroll.
            2. The DialogPanel caps at `max-h-[calc(100vh-2rem)]` and
               its body is `overflow-y-auto`, so on tablets/desktops we
               get the nicer "header + footer pinned, body scrolls"
               feel without ever clipping.
          `min-h-full` on the wrapper keeps centering correct when the
          panel is shorter than the viewport.
        */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.98]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.98]"
            >
              <DialogPanel
                className={cn(
                  'w-full bg-surface text-ink rounded-2xl shadow-elevated border border-line',
                  // Cap height so the body section can scroll
                  // independently. The 1.5rem subtraction matches the
                  // p-3 padding on the wrapper (top+bottom) plus a
                  // little breathing room.
                  'flex flex-col max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2rem)]',
                  SIZES[size],
                )}
              >
                <div className="flex-shrink-0 flex items-start justify-between p-5 border-b border-line">
                  <div className="min-w-0">
                    {title && (
                      <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
                    )}
                    {description && (
                      <p className="text-sm text-ink-soft mt-0.5">{description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="ml-3 -mr-1 -mt-1 p-1 rounded-lg text-ink-soft hover:bg-surface-2"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-5 flex-1 overflow-y-auto overscroll-contain">
                  {children}
                </div>
                {footer && (
                  <div className="flex-shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 py-4 bg-surface-2 border-t border-line rounded-b-2xl">
                    {footer}
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
